"use client"
import { useQualityChanges } from "@/hooks/use-quality-presentations"


import { FormikProvider, useFormik } from "formik"

import { FormInput, FormTextarea } from "@/components/form-inputs"
import { Button } from "@/components/ui/button"
import { DiffLines } from "@/components/ui/prompt-diff"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useAcceptKbSuggestionMutation,
  useRejectSuggestionMutation,
} from "@/hooks/mutations/conversation-quality"
import { usePermissions } from "@/hooks/use-permissions"
import { AI_PERMISSIONS } from "@/lib/auth/permissions.constants"

import type { QualitySuggestionsResult } from "@/server-actions/conversation-quality"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type ArticleSuggestion = Extract<
  QualitySuggestionsResult["suggestions"][number],
  { kind: "kb_addition" }
>

/** The server re-checks both permissions and that the draft is still waiting. */
export function ArticleReviewDialog({
  suggestion,
  open,
  onOpenChange,
}: {
  suggestion: ArticleSuggestion
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { describeWaiting, articleDestination, articleLines } = useQualityChanges()


  const tUi = useTranslations("ConversationQuality.ConversationQualityArticleReviewDialog")

  const { can } = usePermissions()
  const canAddDocuments = can(AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE)
  const close = () => onOpenChange(false)
  const accept = useAcceptKbSuggestionMutation({ onAccepted: close })
  const reject = useRejectSuggestionMutation("kb_addition", { onRejected: close })
  const isDeciding = accept.isPending || reject.isPending
  const { decline } = describeWaiting({
    ...suggestion,
    operation: null,
    edit: null,
    checkReason: null,
    replacing: suggestion.before?.author ?? null,
  })

  const formik = useFormik<ArticleValues>({
    initialValues: { title: suggestion.title, content: suggestion.content },
    onSubmit: async (values, helpers) => {
      try {
        await accept.mutateAsync({
          input: { suggestionId: suggestion.id, ...values },
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
      } catch {
        // withQueryHandling has shown the toast and placed any field errors already.
      }
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={formik.handleSubmit}>
          <DialogHeader>
            <DialogTitle><span key={tUi("reviewArticle")}>{tUi("reviewArticle")}</span></DialogTitle>
            <DialogDescription><TranslationText>{tUi.rich("draftedForValue0FromWhatYourTeamTold", { value0: String(suggestion.agentName), value1: String(suggestion.topicLabel) })}</TranslationText></DialogDescription>
          </DialogHeader>

          <FormikProvider value={formik}>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground break-words">
                {articleDestination(suggestion.agentName, suggestion.topicCategory, suggestion.before)}
              </p>
              <FormInput name="title" label={tUi("title")} />
              <FormTextarea name="content" label={tUi("article")} rows={12} />
              {suggestion.before && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground"><span key={tUi("whatChanges")}>{tUi("whatChanges")}</span></p>
                  <div className="max-h-48 overflow-y-auto rounded-md border bg-muted/30 p-2 font-mono text-xs">
                    <DiffLines lines={articleLines(suggestion.before, formik.values)} />
                  </div>
                </div>
              )}
              {!canAddDocuments && (
                <p className="text-xs text-muted-foreground">
                  <span key={tUi("addingItNeedsPermissionToManageTheKnowledge")}>{tUi("addingItNeedsPermissionToManageTheKnowledge")}</span></p>
              )}
            </div>
          </FormikProvider>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => reject.mutate({ input: { suggestionId: suggestion.id } })}
              disabled={isDeciding}
            >
              {decline}
            </Button>
            <Button type="submit" disabled={isDeciding || !canAddDocuments}>
              <span key={tUi("addToKnowledgeBase")}>{tUi("addToKnowledgeBase")}</span></Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface ArticleValues {
  title: string;
  content: string;
}
